/**
 * A small, safe arithmetic-expression parser — no `eval`, no `Function`.
 *
 * Grammar, loosest binding first:
 *
 *     expression := term (("+" | "−") term)*
 *     term       := unary (("×" | "/") unary | implicit power)*
 *     unary      := ("+" | "−") unary | power
 *     power      := primary ("^" unary)?          right-associative: 2^3^2 = 2^9
 *     primary    := number | constant | variable | function "(" expression ")"
 *                 | "(" expression ")" | "√" primary
 *
 * So −x^2 is −(x²), 2^−1 is ½, and 2πr or 2(a + b) multiply without a star. A name
 * written directly before a bracket must be a known function: `L(1 + a)` is refused
 * with a message rather than silently read as L × (1 + a) or as a function called L.
 * `log` is refused too, because it means ln in mathematics and log₁₀ in engineering and
 * guessing wrong is a silent factor of 2.3.
 */

export type FunctionName =
  | "sqrt"
  | "sin"
  | "cos"
  | "tan"
  | "asin"
  | "acos"
  | "atan"
  | "ln"
  | "log10"
  | "exp"
  | "abs";

export type BinaryOperator = "+" | "-" | "*" | "/" | "^";

export type ExprNode =
  | { kind: "number"; value: number; text: string }
  | { kind: "variable"; name: string }
  | { kind: "negate"; operand: ExprNode }
  | { kind: "binary"; op: BinaryOperator; left: ExprNode; right: ExprNode }
  | { kind: "call"; fn: FunctionName; argument: ExprNode };

export type ExprErrorCode =
  | "empty"
  | "too-long"
  | "too-deep"
  | "unexpected-character"
  | "unexpected-token"
  | "unexpected-end"
  | "missing-paren"
  | "unknown-function"
  | "ambiguous-log";

export interface ExprError {
  code: ExprErrorCode;
  /** The offending text, when there is one. */
  token: string;
}

export type ParseResult =
  | { ok: true; ast: ExprNode; variables: string[] }
  | { ok: false; error: ExprError };

export const MAX_EXPRESSION_LENGTH = 300;
const MAX_DEPTH = 60;

const FUNCTIONS: ReadonlySet<string> = new Set<FunctionName>([
  "sqrt",
  "sin",
  "cos",
  "tan",
  "asin",
  "acos",
  "atan",
  "ln",
  "log10",
  "exp",
  "abs",
]);

const CONSTANTS: Readonly<Record<string, { value: number; text: string }>> = {
  pi: { value: Math.PI, text: "π" },
  π: { value: Math.PI, text: "π" },
  e: { value: Math.E, text: "e" },
};

type Token =
  | { type: "number"; value: number; text: string }
  | { type: "name"; text: string }
  | { type: "op"; text: BinaryOperator }
  | { type: "lparen"; text: "(" }
  | { type: "rparen"; text: ")" }
  | { type: "root"; text: "√" };

const NUMBER = /\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|\.\d+(?:[eE][+-]?\d+)?/y;
const NAME =
  /[A-Za-z_\u0370-\u03BF\u03C1-\u03FF][A-Za-z0-9_\u0370-\u03BF\u03C1-\u03FF]*/y;
/** π is always its own token, so 2πr is 2 × π × r rather than a variable "πr". */
const PI_SIGN = "\u03C0";
const SPACE = /\s/;
const SUPERSCRIPT_RUN = /[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]+/g;
const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const TIMES = /[×·⋅∙]/g;
const DIVIDE = /÷/g;
const MINUS = /[−–]/g;
const DOUBLE_STAR = /\*\*/g;

const OPERATORS: Readonly<Record<string, BinaryOperator>> = {
  "+": "+",
  "-": "-",
  "*": "*",
  "/": "/",
  "^": "^",
};

/** "T²" → "T^(2)", typographic operators → ASCII. */
function normalize(input: string): string {
  return input
    .replace(TIMES, "*")
    .replace(DIVIDE, "/")
    .replace(MINUS, "-")
    .replace(DOUBLE_STAR, "^")
    .replace(SUPERSCRIPT_RUN, (run) => {
      const mapped = [...run]
        .map((ch) => {
          if (ch === "⁻") {
            return "-";
          }
          if (ch === "⁺") {
            return "+";
          }
          return String(SUPERSCRIPT_DIGITS.indexOf(ch));
        })
        .join("");
      return `^(${mapped})`;
    });
}

function readSticky(pattern: RegExp, text: string, at: number): string | null {
  pattern.lastIndex = at;
  const match = pattern.exec(text);
  return match ? match[0] : null;
}

function tokenize(text: string): Token[] | ExprError {
  const tokens: Token[] = [];
  let at = 0;
  while (at < text.length) {
    const ch = text[at];
    if (SPACE.test(ch)) {
      at += 1;
      continue;
    }
    const number = readSticky(NUMBER, text, at);
    if (number !== null) {
      tokens.push({ type: "number", value: Number(number), text: number });
      at += number.length;
      continue;
    }
    if (ch === PI_SIGN) {
      tokens.push({ type: "name", text: ch });
      at += 1;
      continue;
    }
    const name = readSticky(NAME, text, at);
    if (name !== null) {
      tokens.push({ type: "name", text: name });
      at += name.length;
      continue;
    }
    const single = singleCharToken(ch);
    if (single === null) {
      return { code: "unexpected-character", token: ch };
    }
    tokens.push(single);
    at += 1;
  }
  return tokens;
}

function singleCharToken(ch: string): Token | null {
  if (ch === "(") {
    return { type: "lparen", text: "(" };
  }
  if (ch === ")") {
    return { type: "rparen", text: ")" };
  }
  if (ch === "√") {
    return { type: "root", text: "√" };
  }
  const op = OPERATORS[ch];
  return op ? { type: "op", text: op } : null;
}

class ParseFailure extends Error {
  readonly detail: ExprError;
  constructor(detail: ExprError) {
    super(detail.code);
    this.detail = detail;
  }
}

/** Recursive descent over the token list. Throws ParseFailure; `parseExpression` catches. */
class Parser {
  private index = 0;
  private depth = 0;
  private readonly tokens: Token[];
  readonly variables: string[] = [];

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  parseAll(): ExprNode {
    const node = this.expression();
    const extra = this.peek();
    if (extra) {
      this.fail("unexpected-token", extra.text);
    }
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private next(): Token | undefined {
    const token = this.tokens[this.index];
    this.index += 1;
    return token;
  }

  private fail(code: ExprErrorCode, token = ""): never {
    throw new ParseFailure({ code, token });
  }

  private isOp(token: Token | undefined, ...ops: BinaryOperator[]): boolean {
    return token?.type === "op" && ops.includes(token.text);
  }

  private enter() {
    this.depth += 1;
    if (this.depth > MAX_DEPTH) {
      this.fail("too-deep");
    }
  }

  private expression(): ExprNode {
    this.enter();
    let node = this.term();
    while (this.isOp(this.peek(), "+", "-")) {
      const op = this.next()?.text as BinaryOperator;
      node = { kind: "binary", op, left: node, right: this.term() };
    }
    this.depth -= 1;
    return node;
  }

  /** A name, bracket or root straight after a factor multiplies it: 2πr, 3(x + 1). */
  private startsImplicit(token: Token | undefined): boolean {
    return (
      token?.type === "name" ||
      token?.type === "lparen" ||
      token?.type === "root"
    );
  }

  private term(): ExprNode {
    let node = this.unary();
    let token = this.peek();
    while (this.isOp(token, "*", "/") || this.startsImplicit(token)) {
      if (token?.type === "op") {
        this.next();
        node = {
          kind: "binary",
          op: token.text,
          left: node,
          right: this.unary(),
        };
      } else {
        node = { kind: "binary", op: "*", left: node, right: this.power() };
      }
      token = this.peek();
    }
    return node;
  }

  private unary(): ExprNode {
    const token = this.peek();
    if (this.isOp(token, "+")) {
      this.next();
      return this.unary();
    }
    if (this.isOp(token, "-")) {
      this.next();
      this.enter();
      const operand = this.unary();
      this.depth -= 1;
      return { kind: "negate", operand };
    }
    return this.power();
  }

  private power(): ExprNode {
    const base = this.primary();
    if (this.isOp(this.peek(), "^")) {
      this.next();
      this.enter();
      const exponent = this.unary();
      this.depth -= 1;
      return { kind: "binary", op: "^", left: base, right: exponent };
    }
    return base;
  }

  private bracketed(): ExprNode {
    const inner = this.expression();
    if (this.next()?.type !== "rparen") {
      this.fail("missing-paren", ")");
    }
    return inner;
  }

  private named(text: string): ExprNode {
    const following = this.peek();
    if (text === "log") {
      this.fail("ambiguous-log", text);
    }
    if (FUNCTIONS.has(text)) {
      if (following?.type !== "lparen") {
        this.fail("unexpected-token", text);
      }
      this.next();
      return {
        kind: "call",
        fn: text as FunctionName,
        argument: this.bracketed(),
      };
    }
    if (following?.type === "lparen") {
      this.fail("unknown-function", text);
    }
    const constant = CONSTANTS[text];
    if (constant) {
      return { kind: "number", value: constant.value, text: constant.text };
    }
    if (!this.variables.includes(text)) {
      this.variables.push(text);
    }
    return { kind: "variable", name: text };
  }

  private primary(): ExprNode {
    const token = this.next();
    if (!token) {
      return this.fail("unexpected-end");
    }
    if (token.type === "number") {
      return { kind: "number", value: token.value, text: token.text };
    }
    if (token.type === "name") {
      return this.named(token.text);
    }
    if (token.type === "lparen") {
      return this.bracketed();
    }
    if (token.type === "root") {
      this.enter();
      const argument = this.primary();
      this.depth -= 1;
      return { kind: "call", fn: "sqrt", argument };
    }
    return this.fail("unexpected-token", token.text);
  }
}

/** Parse a formula. Variables are listed in the order they first appear. */
export function parseExpression(input: string): ParseResult {
  const trimmed = input.trim();
  if (trimmed === "") {
    return { ok: false, error: { code: "empty", token: "" } };
  }
  if (trimmed.length > MAX_EXPRESSION_LENGTH) {
    return { ok: false, error: { code: "too-long", token: "" } };
  }
  const tokens = tokenize(normalize(trimmed));
  if (!Array.isArray(tokens)) {
    return { ok: false, error: tokens };
  }
  const parser = new Parser(tokens);
  try {
    const ast = parser.parseAll();
    return { ok: true, ast, variables: parser.variables };
  } catch (error) {
    if (error instanceof ParseFailure) {
      return { ok: false, error: error.detail };
    }
    throw error;
  }
}

/* ------------------------------------------------------------ evaluation */

export type AngleUnit = "radians" | "degrees";

const TO_RADIANS = Math.PI / 180;

function callFunction(fn: FunctionName, x: number, unit: AngleUnit): number {
  const inScale = unit === "degrees" ? TO_RADIANS : 1;
  const outScale = unit === "degrees" ? 1 / TO_RADIANS : 1;
  switch (fn) {
    case "sqrt":
      return Math.sqrt(x);
    case "sin":
      return Math.sin(x * inScale);
    case "cos":
      return Math.cos(x * inScale);
    case "tan":
      return Math.tan(x * inScale);
    case "asin":
      return Math.asin(x) * outScale;
    case "acos":
      return Math.acos(x) * outScale;
    case "atan":
      return Math.atan(x) * outScale;
    case "ln":
      return Math.log(x);
    case "log10":
      return Math.log10(x);
    case "exp":
      return Math.exp(x);
    default:
      return Math.abs(x);
  }
}

function binary(op: BinaryOperator, left: number, right: number): number {
  switch (op) {
    case "+":
      return left + right;
    case "-":
      return left - right;
    case "*":
      return left * right;
    case "/":
      return left / right;
    default:
      return left ** right;
  }
}

/**
 * The formula's value. NaN or ±Infinity when it leaves its domain (√ of a negative,
 * ln 0, division by zero) — callers check `Number.isFinite`.
 */
export function evaluate(
  node: ExprNode,
  env: Readonly<Record<string, number>>,
  unit: AngleUnit = "radians"
): number {
  switch (node.kind) {
    case "number":
      return node.value;
    case "variable":
      return env[node.name] ?? Number.NaN;
    case "negate":
      return -evaluate(node.operand, env, unit);
    case "binary":
      return binary(
        node.op,
        evaluate(node.left, env, unit),
        evaluate(node.right, env, unit)
      );
    default:
      return callFunction(node.fn, evaluate(node.argument, env, unit), unit);
  }
}

/* --------------------------------------------------------------- display */

const PRECEDENCE: Record<BinaryOperator, number> = {
  "+": 1,
  "-": 1,
  "*": 2,
  "/": 2,
  "^": 4,
};

const SHOWN_OP: Record<BinaryOperator, string> = {
  "+": " + ",
  "-": " − ",
  "*": " × ",
  "/": " / ",
  "^": "^",
};

function precedenceOf(node: ExprNode): number {
  if (node.kind === "binary") {
    return PRECEDENCE[node.op];
  }
  return node.kind === "negate" ? 3 : 5;
}

function wrap(node: ExprNode, needed: boolean): string {
  const text = formatExpression(node);
  return needed ? `(${text})` : text;
}

/**
 * The formula as it was understood, with every operator explicit: "2πr" reads back as
 * "2 × π × r". Brackets appear only where precedence needs them.
 */
export function formatExpression(node: ExprNode): string {
  switch (node.kind) {
    case "number":
      return node.text;
    case "variable":
      return node.name;
    case "negate":
      return `−${wrap(node.operand, precedenceOf(node.operand) < 3)}`;
    case "binary": {
      const own = PRECEDENCE[node.op];
      const left = wrap(
        node.left,
        node.op === "^"
          ? precedenceOf(node.left) <= own
          : precedenceOf(node.left) < own
      );
      const rightNeeds =
        node.op === "^"
          ? precedenceOf(node.right) < own
          : precedenceOf(node.right) < own ||
            (precedenceOf(node.right) === own &&
              (node.op === "-" || node.op === "/"));
      return `${left}${SHOWN_OP[node.op]}${wrap(node.right, rightNeeds)}`;
    }
    default:
      return `${node.fn}(${formatExpression(node.argument)})`;
  }
}
